<template>
  <div class="history-wrapper safe-area-bottom" :class="{ 'is-deleting': isConfirmingDeletion }">
    <!-- Head -->
    <div class="flex w-full relative items-center justify-center mt-1 p-3">
      <span class="text-xl font-semibold tracking-wider">{{ title }}</span>
      <img
        src="../../assets/common/close-icon.svg"
        class="absolute right-0 mr-6"
        alt="Close"
        @click="$emit('close')"
      />
    </div>
    <!-- Body snapshot -->
    <div class="snapshot-wrapper">
      <img class="snapshot" :class="{ broken: noSnapshot }" :src="snapshotUrl" :alt="record.tid" />
    </div>
    <!-- Body Provider-->
    <div class="body-provider">
      <p>{{ providerText }}</p>
    </div>
    <!-- Timeline -->
    <div class="timeline" ref="swiper">
      <div class="swiper-wrapper">
        <!-- Slides -->
        <div
          v-for="(r, index) in reversedRecords"
          :key="r.tid"
          class="swiper-slide"
          :class="{ active: r.tid === tid }"
          @click="gotoSlide(index)"
        >
          <span v-if="r.year" class="year">{{ r.year }}</span>
          <span>{{ r.created_at | toDate }}</span>
          <span>{{ r.created_at | toTime }}</span>
        </div>
      </div>
    </div>
    <!-- Button -->
    <template v-if="isConfirmingDeletion">
      <div class="w-full flex mt-auto mb-3 justify-around">
        <c-button width="45%" secondary @click="confirmDeletion">
          <span>{{ $t('homePage.delete') }}</span>
        </c-button>
        <c-button width="45%" @click="cancelDeletion">
          <span>{{ $t('homePage.cancel_delete') }}</span>
        </c-button>
      </div>
    </template>
    <div class="w-full flex justify-end" v-else>
      <c-button class="mt-auto mb-3 mx-auto" @click="choose" :confirmedStatus="confirmedStatus">
        <span>{{ $t('homePage.view_avatar') }}</span>
      </c-button>
      <!-- Delete Button -->
      <img
        class="icon-btn absolute mr-3"
        :src="leftBtnSrc"
        alt="Delete"
        @click="onLeftBtnClicked"
      />
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import Swiper from 'swiper';
import 'swiper/swiper.min.css';

import i18n from '@/i18n';

import CButton from '@/components/common/CButton.vue';
import brokenIcon from '@/assets/common/icon-broken.svg';
import defaultIcon from '@/assets/common/default-snap.svg';
import deleteIcon from '@/assets/common/icon-bin.svg';
import backIcon from '@/assets/DefaultCloudzet/HomePage/arrow-back.svg';

const getYear = dateString => new Date(dateString).getFullYear();

export default {
  name: 'History',
  props: {
    records: Array,
    tid: String,
  },
  components: {
    CButton,
  },
  filters: {
    toDate(value) {
      const { locale } = i18n;
      const dateObj = new Date(value);
      return new Intl.DateTimeFormat(locale, { month: 'short', day: '2-digit' }).format(dateObj);
    },
    toTime(value) {
      const { locale } = i18n;
      const dateObj = new Date(value);
      return new Intl.DateTimeFormat(locale, { timeStyle: 'short', hour12: false }).format(dateObj);
    },
  },
  data: () => ({
    index: 0,
    reversedRecords: [],
    isConfirmingDeletion: false,
  }),
  beforeDestroy() {
    this.destroySwiper();
  },
  mounted() {
    this.initSwiper();
  },
  methods: {
    choose() {
      this.$emit('choose', { ...this.record });
    },
    destroySwiper() {
      if (!this.$swiper) return;

      this.$swiper.destroy();
      this.$swiper = null;
    },
    initSwiper() {
      const initialIndex = this.reversedRecords.findIndex(record => record.tid === this.tid);
      this.$swiper = new Swiper(this.$refs.swiper, {
        slidesPerView: 5,
        centeredSlides: true,
        initialSlide: initialIndex,
        on: {
          activeIndexChange: () => {
            if (!this.$swiper) return;

            this.index = this.$swiper.activeIndex;
          },
        },
      });
      this.index = initialIndex;
    },
    updateSwiper(newIndex) {
      this.$nextTick(() => {
        if (!this.$swiper) return;

        this.$swiper.update();
        this.$swiper.slideTo(newIndex);
      });
    },
    gotoSlide(index) {
      if (!this.$swiper) return;

      this.$swiper.slideTo(index);
    },
    onLeftBtnClicked() {
      if (!this.record) return;

      this.isConfirmingDeletion = !this.isConfirmingDeletion;
    },
    confirmDeletion() {
      this.$emit('delete', this.record.tid);
    },
    cancelDeletion() {
      this.isConfirmingDeletion = false;
    },
  },
  watch: {
    isConfirmingDeletion(nv) {
      if (!this.$swiper) return;

      if (nv) {
        this.$swiper.disable();
        this.$swiper.slides[this.$swiper.activeIndex].classList.add('swiper-slide-hide-border');
      } else {
        this.$swiper.enable();
        this.$swiper.slides[this.$swiper.activeIndex].classList.remove('swiper-slide-hide-border');
      }
    },
    records: {
      handler(nv, ov) {
        this.isConfirmingDeletion = false;
        if (!Array.isArray(nv)) return;

        let year = '';
        const previousIndex = this.index;
        this.reversedRecords = nv.reduce((acc, record, index) => {
          const firstOrLast = index === 0 || index === nv.length - 1;
          const currentYear = getYear(record.created_at);
          if (!year || year !== currentYear || firstOrLast) {
            record.year = currentYear; // eslint-disable-line
            year = currentYear;
          }
          acc.unshift(record);
          return acc;
        }, []);
        if (!Array.isArray(ov) || ov.length === 0) return;

        this.updateSwiper(previousIndex > 0 ? previousIndex - 1 : 0);
      },
      immediate: true,
    },
    tid(nv) {
      if (!nv) return;

      this.updateSwiper(this.reversedRecords.findIndex(r => r.tid === nv));
    },
  },
  computed: {
    ...mapState('app', ['scannersConfigs']),
    record() {
      return this.reversedRecords[this.index] || {};
    },
    scannerId() {
      const { uuid = '' } = this.record.scanner || {};
      return uuid;
    },
    shaderType() {
      let type = 'default';
      if (this.scannerId && this.scannersConfigs[this.scannerId]) {
        type = this.scannersConfigs[this.scannerId].shader_type || 'default';
      }

      return type;
    },
    noSnapshot() {
      return !this.record.icon_url;
    },
    snapshotUrl() {
      if (this.shaderType !== 'default') return defaultIcon;
      return this.record?.icon_url || brokenIcon;
    },
    leftBtnSrc() {
      return this.isConfirmingDeletion ? backIcon : deleteIcon;
    },
    title() {
      return this.isConfirmingDeletion
        ? this.$t('homePage.confirm_delete')
        : this.$t('homePage.history');
    },
    providerText() {
      if (this.record === null) return 'unknown';
      return this.record.provider
        ? this.$t('mobileScan.mobile_scan')
        : this.$t('mobileScan.body_scanner');
    },
    confirmedStatus() {
      return this.record.tid !== this.tid;
    },
  },
};
</script>

<style lang="scss" scoped>
.history-wrapper {
  @apply flex flex-1 w-full flex-col;
}
.snapshot {
  @apply mx-auto;
  max-width: 150px;
  height: 152px;
}
.snapshot-wrapper {
  @apply relative;
}
.body-provider {
  @apply inline-flex font-bold text-xxs px-2 mt-1;
  width: fit-content;
  align-self: center;
  background: rgba(237, 239, 255, 0.8);
  border-radius: 12px;
  align-items: center;
}
.body-provider p {
  background: linear-gradient(270deg, #2245ff 0.85%, #4300d9 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-fill-color: transparent;
}
.timeline {
  @apply w-full mt-8 mb-4 relative;
}
.is-deleting {
  .snapshot-wrapper::after {
    @apply absolute w-full h-full top-0 left-0 right-0 mx-auto;
    content: '';
    display: inline-block;
    background: url('../../assets/common/icon-scratch.svg') center no-repeat;
  }
  .swiper-slide {
    @apply invisible;
    &.swiper-slide-active {
      @apply visible;
    }
  }
}
.swiper-slide {
  @apply relative text-center text-sm;
  padding: 9px 0;
  color: #bfbfbf;
  &.swiper-slide-active,
  &.active {
    @apply font-semibold;
  }
  &.swiper-slide-active {
    color: #262626;
    &:before {
      @apply block absolute h-full left-0 right-0;
      content: '';
      margin-left: -10%;
      width: 120%;
      top: 50%;
      transform: translateY(-50%);
      border: 3px solid #540deb;
      border-radius: 60px;
    }
  }
  &.swiper-slide-hide-border {
    &:before {
      border: 0;
    }
  }
  &.active {
    color: #540deb;
  }
  .year {
    @apply absolute mx-auto;
    top: -40%;
    left: 0;
    right: 0;
  }
  span {
    @apply block my-1 text-center;
  }
}
.icon-btn {
  @apply p-3;
  min-height: 45px;
}
.broken {
  opacity: 0.5;
  max-width: 100px;
}
</style>
